/**
 * The audio playback half of the sound feature: owns the Web Audio graph and plays
 * the {@link SoundEvent}s the pure {@link SoundObserver} decides on. Kept
 * deliberately thin and free of game logic - it knows nothing about footsteps,
 * doors, or distance, only "play this decoded buffer at this gain" - so all the
 * testable decision-making lives in the observer and this layer is a small,
 * imperative shell around the browser API (which jsdom lacks, so it is largely not
 * unit tested, like the socket glue in `gameClient`; the master-mute gate is the one
 * branch pinned, against a stub `AudioContext`, in `awardCue.test.ts`).
 *
 * One {@link AudioContext} feeds a master {@link GainNode} feeding the speakers;
 * each event spins up a throwaway `AudioBufferSourceNode` -> per-event `GainNode`
 * (the proximity volume) -> master, so any number of footsteps and doors overlap
 * cleanly. The context starts **suspended** under the browser autoplay policy and
 * is woken by {@link resume} on the first user gesture (the splash submit / a click).
 */
import { fetchSoundBuffer } from './soundAssets';
import { SoundResidency, DEFAULT_SOUND_BUDGET_BYTES } from './soundResidency';
import { LOBBY_WARM_SOUNDS, warmSoundsForMode, type SoundWarmMode } from './soundWarmSets';
import type { SoundEvent, SoundId } from './soundObserver';

/**
 * The loudness ceiling applied on top of each event's proximity volume, so the whole
 * soundscape sits politely under speech/UI even at full volume. The user's volume slider
 * (settings panel, ADR-0105) rides *within* this ceiling: effective master gain = `level * MASTER_CEILING`,
 * so a fresh player at full level (1) hears exactly the historical 0.7 and can only ever
 * turn it down from there. One knob to keep the mix polite.
 */
const MASTER_CEILING = 0.7;

/**
 * A very short gain ramp (seconds) at the start of every sound. The UO samples do
 * not begin on a zero-crossing, so jumping the gain straight to full at sample 0
 * produces an audible click/pop on the attack; ramping from 0 over a few
 * milliseconds removes it while staying short enough that the footfall/door still
 * reads as crisp, not faded-in. A matching micro-fade is not applied at the tail -
 * the samples already decay to near-silence.
 */
const ATTACK_SECONDS = 0.006;

export class AudioManager {
  private ctx?: AudioContext;
  private master?: GainNode;
  // The bounded decoded-sound residency (issue #626): lazy per-id decode, warm
  // sets, byte accounting, and LRU eviction. Created with the context in open().
  private residency?: SoundResidency;
  private readonly budgetBytes: number;
  // The active mode whose critical warm set rides on top of the lobby set.
  private mode: SoundWarmMode | null = null;
  private loaded = false;

  constructor(opts: { soundBudgetBytes?: number } = {}) {
    this.budgetBytes = opts.soundBudgetBytes ?? DEFAULT_SOUND_BUDGET_BYTES;
  }
  // The master mute flag, driven by the settings panel's mute button (persisted in
  // localStorage by `sound.ts`, ADR-0105). Stored independently of the audio graph so it can
  // be set before `load()` has created the master gain - whichever happens first, the
  // other applies it: `load()` reads this when wiring the master, `setMuted()` writes
  // through to a live master. When muted the master gain is pinned to 0 (so any sound
  // already scheduled goes silent too) and `play()` short-circuits.
  private muted = false;
  // The user's master volume level in `[0, 1]`, driven by the settings panel's volume slider
  // (persisted in localStorage by `sound.ts`, ADR-0105). Independent of {@link muted}: muting
  // gates the output to silence without disturbing this level, so unmuting restores the
  // chosen loudness. Stored separately from the audio graph (like {@link muted}) so it
  // can be set before `load()` builds the master gain - whichever happens first, the
  // other applies it. The effective master gain is `level * MASTER_CEILING` when audible.
  private level = 1;

  /**
   * Create the {@link AudioContext} + master gain (idempotent) and return the context,
   * **without** loading any sound buffers. Split out of {@link load} for two reasons: the
   * music player can init on the shared context in parallel with the (much larger)
   * sound-buffer decode instead of waiting behind it, and the context is created lazily on
   * the first user gesture, which is the clean autoplay start (no "AudioContext was not
   * allowed to start" warning). A browser without Web Audio returns `undefined` and marks
   * the manager a silent no-op. The context starts suspended; {@link resume} wakes it.
   */
  open(): AudioContext | undefined {
    if (this.ctx) return this.ctx;
    const Ctor = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) {
      console.warn('Web Audio unavailable - sound disabled');
      this.loaded = true;
      return undefined;
    }
    this.ctx = new Ctor();
    const ctx = this.ctx;
    this.residency = new SoundResidency({
      decode: (id) => fetchSoundBuffer(ctx, id),
      budgetBytes: this.budgetBytes,
    });
    this.master = this.ctx.createGain();
    // Honour a mute/level chosen before the graph existed (the persisted mute toggle
    // and volume slider are applied at startup, often before the buffers finish loading).
    this.master.gain.value = this.masterGain();
    this.master.connect(this.ctx.destination);
    return this.ctx;
  }

  /**
   * Open the context (if needed) and warm the **documented common-lobby sound set**
   * ({@link LOBBY_WARM_SOUNDS}) - NOT the whole registry (issue #626): everything
   * else decodes lazily on first play and is LRU-evicted once idle, bounding a long
   * session's decoded-audio memory. Best called on the first user gesture (deferred
   * off the page-load / splash critical path, where the visual assets need the
   * bandwidth and nothing is audible yet anyway). Idempotent, and a browser without
   * Web Audio (or a decode failure) degrades to a silent no-op manager rather than
   * throwing - audio is a nicety, never load-bearing.
   */
  async load(): Promise<void> {
    if (this.loaded) return;
    const ctx = this.open();
    this.loaded = true;
    if (!ctx) return; // no Web Audio - open() already marked us a silent no-op
    await this.applyWarmSet();
  }

  /**
   * Warm a mode's **feature-critical sounds** at mode entry/readiness (issue #626):
   * the Arena run-active edge / Sphere or card match-active edge pass their mode,
   * and every mode-leave path passes `null`. The residency layer's
   * current warm set becomes `LOBBY ∪ mode set`, so the previous mode's set loses
   * its protection (ordinary LRU-evictable) while the new one decodes ahead of its
   * first required use. Idempotent per mode; safe before {@link load} (the mode is
   * remembered and warmed at activation). Fire-and-forget for callers; the resolved
   * promise (all decodes settled) is for tests.
   */
  warmMode(mode: SoundWarmMode | null): Promise<void> {
    if (mode === this.mode) return Promise.resolve();
    this.mode = mode;
    if (!this.loaded) return Promise.resolve(); // applied by load() at activation
    return this.applyWarmSet();
  }

  /**
   * Best-effort preload for a small, presently visible working set. Unlike a
   * mode warm set these sounds are not pinned: they simply decode early and
   * remain subject to the normal byte budget and deterministic LRU eviction.
   * Card battles use this when a minion first appears, giving its attack/hurt/
   * death family time to settle before the creature can act.
   */
  async preload(ids: readonly SoundId[]): Promise<void> {
    const residency = this.residency;
    if (!this.loaded || !residency) return;
    await Promise.all([...new Set(ids)].map((id) => residency.acquire(id)));
  }

  /** Push the current lobby+mode warm set into the residency layer. */
  private applyWarmSet(): Promise<void> {
    const residency = this.residency;
    if (!residency) return Promise.resolve();
    return residency.setWarmSet([...LOBBY_WARM_SOUNDS, ...warmSoundsForMode(this.mode)]);
  }

  /** Total decoded sound PCM bytes currently resident (metrics/tests). */
  decodedSoundBytes(): number {
    return this.residency?.totalBytes() ?? 0;
  }

  /** The resident decoded sound ids (metrics/tests). */
  residentSoundIds(): string[] {
    return this.residency?.residentIds() ?? [];
  }

  /**
   * Resume the audio context, to be called from a user-gesture handler (the
   * autoplay policy blocks audio until then). Idempotent and harmless if the
   * context is already running or absent.
   */
  resume(): void {
    if (this.ctx?.state === 'suspended') void this.ctx.resume();
  }

  /**
   * The shared {@link AudioContext}, or `undefined` until {@link load} has created it
   * (or on a browser without Web Audio). Exposed so the background-music player
   * (ADR-0146) can build its **own** gain node on the **same** context - one context
   * for the whole app - and drive it independently of the SFX master, so the music
   * volume/mute slider affects only music. The music player owns its own graph from
   * here down; it never touches the SFX {@link master}.
   */
  context(): AudioContext | undefined {
    return this.ctx;
  }

  /**
   * Mute or unmute the whole soundscape, driven by the settings panel's mute button. Pins
   * the master gain to 0 (or back to the chosen {@link level}) so anything mid-flight
   * also goes silent, and records the flag so a mute set before {@link load} still
   * takes effect once the graph exists. The volume {@link level} is left untouched, so
   * unmuting restores exactly the prior loudness. Safe to call any time.
   */
  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.master) this.master.gain.value = this.masterGain();
  }

  /** Whether the soundscape is currently muted. */
  isMuted(): boolean {
    return this.muted;
  }

  /**
   * Set the master volume level (`[0, 1]`, clamped), driven by the settings panel's
   * volume slider. Rides within {@link MASTER_CEILING}, independent of {@link muted}: when muted
   * the output stays silent and this level is simply remembered for the next unmute.
   * Records the value so a level set before {@link load} takes effect once the graph
   * exists. Safe to call any time.
   */
  setVolume(level: number): void {
    this.level = Math.min(1, Math.max(0, level));
    if (this.master) this.master.gain.value = this.masterGain();
  }

  /** The current master volume level in `[0, 1]` (independent of mute). */
  getVolume(): number {
    return this.level;
  }

  /** The audible master gain: silence when muted, else the level within the ceiling. */
  private masterGain(): number {
    return this.muted ? 0 : this.level * MASTER_CEILING;
  }

  /**
   * Play one sound at `volume` (`[0, 1]`, already proximity-scaled by the
   * observer). A resident (warm or recently used) sound starts synchronously; a
   * cold one lazily decodes first and starts when ready (a one-time, millisecond-
   * scale latency - and how an evicted sound reloads). A terminally absent sound
   * (unbaked id / decode failure) or a context that never initialised is a silent
   * no-op, never a retry loop. Each play uses a fresh source + gain node, so
   * overlapping sounds mix instead of cutting each other off; each live source
   * holds a playback reference that shields its buffer from eviction until it ends.
   */
  play(event: SoundEvent): void {
    const residency = this.residency;
    if (!residency || !this.ctx || !this.master || this.muted || event.volume <= 0) return;
    const buffer = residency.get(event.sound);
    if (buffer) {
      this.playBuffer(event, buffer);
      return;
    }
    void residency.acquire(event.sound).then((decoded) => {
      if (decoded) this.playBuffer(event, decoded);
    });
  }

  /** Start one decoded buffer for `event`, tracking its playback reference. */
  private playBuffer(event: SoundEvent, buffer: AudioBuffer): void {
    const ctx = this.ctx;
    const master = this.master;
    // Re-checked for the lazy path: a mute flipped while the decode was in flight
    // must still silence the deferred start.
    if (!ctx || !master || this.muted) return;
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    if (event.rate !== undefined) source.playbackRate.value = event.rate;
    const gain = ctx.createGain();
    // Ramp from silence to the target volume over a few ms to kill the attack
    // click (the samples don't start on a zero-crossing), then hold.
    const now = ctx.currentTime;
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(event.volume, now + ATTACK_SECONDS);
    source.connect(gain).connect(master);
    // A playing sound is never evicted (issue #626): hold a reference for the
    // voice's lifetime, released on its natural end.
    this.residency?.beginPlayback(event.sound);
    source.onended = () => this.residency?.endPlayback(event.sound);
    source.start(now);
  }

  /** Play a batch of events (one snapshot's worth) in order. */
  playAll(events: readonly SoundEvent[]): void {
    for (const event of events) this.play(event);
  }
}
